import React, { useState, useRef } from "react";

const ContactForm = () => {
  const [formStatus, setFormStatus] = useState(null); // To track form submission status
  const [loading, setLoading] = useState(false); // Loading state
  const formRef = useRef(null); // Reference to form for scrolling

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); // Start loading
    const formData = new FormData(e.target);

    try {
      const response = await fetch("https://formsubmit.co/info@getoffmypavement.co.uk", {
        method: "POST",
        body: formData,
        headers: {
          Accept: "application/json",
        },
      });

      if (response.ok) {
        setFormStatus("success");
        e.target.reset(); // Clear form
      } else {
        setFormStatus("error");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setFormStatus("error");
    } finally {
      setLoading(false); // Stop loading
      formRef.current.scrollIntoView({ behavior: "smooth" }); // Scroll to top
    }
  };

  return (
    <div ref={formRef} className="max-w-3xl mt-10 mx-auto p-6 bg-white shadow-md rounded-md">
      <h2 className="text-xl font-semibold mb-4 text-gray-800">Contact Us</h2>

      {/* Status Messages */}
      {formStatus === "success" && (
        <div className="p-3 mb-4 text-green-700 bg-green-100 rounded">
          Thank you! Your message has been sent successfully.
        </div>
      )}
      {formStatus === "error" && (
        <div className="p-3 mb-4 text-red-700 bg-red-100 rounded">
          Oops! Something went wrong. Please try again later.
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Hidden inputs for FormSubmit */}
        <input type="hidden" name="_captcha" value="false" />
        <input type="hidden" name="_subject" value="New Contact Form Submission" />

        {/* Name and Phone */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="name" className="text-sm font-medium text-gray-700">Name</label>
            <input
              type="text"
              id="name"
              name="Name"
              required
              placeholder="Your full name"
              className="mt-1 px-3 py-2 border-2 border-gray-400 rounded-md text-sm w-full"
            />
          </div>
          <div>
            <label htmlFor="contact-number" className="text-sm font-medium text-gray-700">Phone Number</label>
            <input
              type="tel"
              id="contact-number"
              name="Contact Number"
              required
              placeholder="Your phone number"
              className="mt-1 px-3 py-2 border-2 border-gray-400 rounded-md text-sm w-full"
            />
          </div>
        </div>

        {/* Address */}
        <div>
          <label htmlFor="address" className="text-sm font-medium text-gray-700">Address</label>
          <input
            type="text"
            id="address"
            name="Address"
            required
            placeholder="Your address"
            className="mt-1 px-3 py-2 border-2 border-gray-400 rounded-md text-sm w-full"
          />
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email" className="text-sm font-medium text-gray-700">Email Address</label>
          <input
            type="email"
            id="email"
            name="Email"
            required
            placeholder="Your email address"
            className="mt-1 px-3 py-2 border-2 border-gray-400 rounded-md text-sm w-full"
          />
        </div>

        {/* Date and Location */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="date-of-obstruction" className="text-sm font-medium text-gray-700">Date of Obstruction</label>
            <input
              type="date"
              id="date-of-obstruction"
              name="Date of Obstruction"
              required
              className="mt-1 px-3 py-2 border-2 border-gray-400 rounded-md text-sm w-full"
            />
          </div>
          <div>
            <label htmlFor="location-of-obstruction" className="text-sm font-medium text-gray-700">Location of Obstruction</label>
            <input
              type="text"
              id="location-of-obstruction"
              name="Location of Obstruction"
              required
              placeholder="Location details"
              className="mt-1 px-3 py-2 border-2 border-gray-400 rounded-md text-sm w-full"
            />
          </div>
        </div>

        {/* Pictures */}
        <div>
          <label htmlFor="pictures" className="text-sm font-medium text-gray-700">Pictures of Vehicle (Optional)</label>
          <input
            type="file"
            id="pictures"
            name="Pictures of Vehicle"
            className="mt-1 text-sm border-2 border-gray-400 rounded-md file:mr-4 file:py-2 file:px-4 file:bg-indigo-50 file:text-indigo-600 hover:file:bg-indigo-100"
            accept="image/*"
            multiple
          />
        </div>

        {/* Time Available for Call */}
        <div>
          <label htmlFor="time-available" className="text-sm font-medium text-gray-700">Time Available for Call</label>
          <select
            id="time-available"
            name="Time Available for Call"
            required
            className="mt-1 px-3 py-2 border-2 border-gray-400 rounded-md text-sm w-full"
          >
            <option value="">Select time</option>
            <option value="morning">Morning</option>
            <option value="afternoon">Afternoon</option>
            <option value="evening">Evening</option>
          </select>
        </div>

        {/* Submit Button with Spinner */}
        <div className="flex justify-center">
          <button
            type="submit"
            disabled={loading}
            className="w-full sm:w-auto flex items-center justify-center px-6 py-2 bg-cyan-900 text-white rounded-md text-sm font-medium shadow hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            {loading ? (
              <>
                <svg className="animate-spin h-5 w-5 mr-2 border-white border-t-transparent border-4 rounded-full" viewBox="0 0 24 24"></svg>
                Sending...
              </>
            ) : (
              "Submit"
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContactForm;
